import React from 'react';
import { ExternalLink, ChevronRight, Info } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatScorePill, shouldDisplayScorePill } from '@/lib/idea-card-score-formatter';
import type { IdeaCardScoreUnit, ScoreRange } from '@/lib/idea-card-types';

interface AccordionTriggerViewProps {
  title: string;
  url?: string;
  index?: number;
  isHighlighted?: boolean;
  score?: number | ScoreRange;
  scoreUnit?: IdeaCardScoreUnit;
  scoreLabel?: string;
  infoTooltip?: string;
  showChevron?: boolean;
}

export const AccordionTriggerView: React.FC<AccordionTriggerViewProps> = ({
  title,
  url,
  index,
  isHighlighted,
  score,
  scoreUnit,
  scoreLabel,
  infoTooltip,
  showChevron = true,
}) => {
  return (
    <div className="flex items-center justify-between w-full bg-neutral-800/50 px-4 py-3">
      <div className="flex-1 min-w-0 flex items-center gap-3">
        {index !== undefined && (
          <span className={cn("font-semibold", isHighlighted ? "text-highlight" : "text-white")}>{index + 1}.</span>
        )}
        <div className="flex-1 min-w-0 flex items-center gap-2">
          <h3 className={cn("font-lg text-sm truncate", isHighlighted ? "text-highlight" : "text-white")}>
            {title}
          </h3>
          {infoTooltip && (
            <Info
              size={14}
              className="text-neutral-400 cursor-help flex-shrink-0"
              title={infoTooltip}
            />
          )}
          {url && (
            <div className="text-secondary hover:text-blue-800 hover:underline flex items-center gap-1 truncate">
              <ExternalLink size={12} />
              {url}
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-3">
        {shouldDisplayScorePill(score) && (
          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-[#CCFF00] text-green-900">
            {formatScorePill(score, scoreUnit, scoreLabel)}
          </span>
        )}
        {showChevron && (
          <ChevronRight className='h-4 w-4 text-zinc-50 transition-transform duration-200 group-data-expanded:rotate-90' />
        )}
      </div>
    </div>
  );
};
